/**
 * SuperBatardComptaDashboard - Admin interface for Rachel v2
 * 
 * Provides admin access to:
 * - Business config (keywords management)
 * - Learnings management
 * - Audit log viewer
 * - Stats overview
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Brain, 
  Settings, 
  History, 
  BookOpen,
  Plus,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Building2,
  Search,
  ChevronDown,
  Download,
  Filter,
  Eye,
  Clock,
  ThumbsUp,
  ThumbsDown,
  Lightbulb
} from 'lucide-react';
import { apiGet, apiPost, apiDelete, getCurrentTenant, initializeTenant } from './utils/apiFetch';

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function SuperBatardComptaDashboard() {
  // State
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('proposals');
  
  // Data
  const [config, setConfig] = useState(null);
  const [learnings, setLearnings] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [stats, setStats] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [proposalStats, setProposalStats] = useState({});

  // ═══════════════════════════════════════════════════════════════
  // INIT
  // ═══════════════════════════════════════════════════════════════

  useEffect(() => {
    async function init() {
      try {
        const t = await initializeTenant();
        setTenant(t);
        if (t) {
          await loadAllData();
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    init();
    
    // Reset scroll on section change
    window.scrollTo(0, 0);
  }, [activeSection]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [configRes, learningsRes, auditRes, statsRes, proposalsRes] = await Promise.all([
        apiGet('/api/rachel-v2/config'),
        apiGet('/api/rachel-v2/learnings'),
        apiGet('/api/rachel-v2/audit-log?limit=50'),
        apiGet('/api/rachel-v2/stats'),
        apiGet('/api/rachel-v2/proposals?status=all')
      ]);
      
      if (configRes.success) setConfig(configRes.config);
      if (learningsRes.success) setLearnings(learningsRes.learnings);
      if (auditRes.success) setAuditLog(auditRes.logs);
      if (statsRes.success) setStats(statsRes);
      if (proposalsRes.success) {
        setProposals(proposalsRes.proposals);
        setProposalStats(proposalsRes.stats || {});
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // LOADING
  // ═══════════════════════════════════════════════════════════════

  if (loading && !tenant) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-purple-500 mx-auto mb-2" />
          <p className="text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Super Batard Compta</h1>
                <p className="text-sm text-gray-400 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {tenant?.name || 'Loading...'} • Rachel v2 Admin
                </p>
              </div>
            </div>
            
            <button
              onClick={loadAllData}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>
      </header>

      {/* Error */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 mt-4">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <aside className="w-64 flex-shrink-0">
            <nav className="space-y-1">
              {[
                { id: 'proposals', label: 'Propositions IA', icon: <Lightbulb className="w-4 h-4" />, badge: proposalStats.pending_review },
                { id: 'config', label: 'Configuration', icon: <Settings className="w-4 h-4" /> },
                { id: 'learnings', label: 'Apprentissages', icon: <Brain className="w-4 h-4" /> },
                { id: 'audit', label: 'Audit Log', icon: <History className="w-4 h-4" /> },
                { id: 'stats', label: 'Statistiques', icon: <BookOpen className="w-4 h-4" /> },
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${
                    activeSection === item.id
                      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  {item.icon}
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="px-2 py-0.5 text-xs bg-orange-500/20 text-orange-400 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {activeSection === 'proposals' && (
              <ProposalsSection
                proposals={proposals}
                stats={proposalStats}
                onReload={loadAllData}
                setError={setError}
              />
            )}
            {activeSection === 'config' && (
              <ConfigSection 
                config={config} 
                onReload={loadAllData}
                setError={setError}
              />
            )}
            {activeSection === 'learnings' && (
              <LearningsSection 
                learnings={learnings}
                onReload={loadAllData}
                setError={setError}
              />
            )}
            {activeSection === 'audit' && (
              <AuditSection auditLog={auditLog} />
            )}
            {activeSection === 'stats' && (
              <StatsSection stats={stats} config={config} />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PROPOSALS SECTION - AI Proposals Review
// ═══════════════════════════════════════════════════════════════

function ProposalsSection({ proposals, stats, onReload, setError }) {
  const [filter, setFilter] = useState('pending_review');
  const [processingId, setProcessingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);

  const approveProposal = async (proposal) => {
    setProcessingId(proposal.normalized_key);
    try {
      const response = await apiPost('/api/rachel-v2/proposals/approve', {
        kind: proposal.kind,
        normalized_key: proposal.normalized_key
      });
      if (response.success) {
        await onReload();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const rejectProposal = async (proposal) => {
    setProcessingId(proposal.normalized_key);
    try {
      const response = await apiPost('/api/rachel-v2/proposals/reject', {
        kind: proposal.kind,
        normalized_key: proposal.normalized_key,
        reason: rejectReason || null
      });
      if (response.success) {
        setShowRejectModal(null);
        setRejectReason('');
        await onReload();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const filteredProposals = filter === 'all'
    ? proposals
    : proposals.filter(p => p.status === filter);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_review':
        return <span className="px-2 py-0.5 text-xs rounded bg-orange-500/20 text-orange-400 flex items-center gap-1"><Clock className="w-3 h-3" /> En attente</span>;
      case 'approved':
        return <span className="px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Approuvé</span>;
      case 'rejected':
        return <span className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejeté</span>;
      default:
        return <span className="px-2 py-0.5 text-xs rounded bg-gray-500/20 text-gray-400">{status}</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Banner */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 p-4 rounded-xl border border-orange-500/20">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-orange-400" />
            <p className="text-sm text-orange-300">En attente</p>
          </div>
          <p className="text-3xl font-bold text-orange-400">{stats.pending_review || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 p-4 rounded-xl border border-green-500/20">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <p className="text-sm text-green-300">Approuvées</p>
          </div>
          <p className="text-3xl font-bold text-green-400">{stats.approved || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 p-4 rounded-xl border border-red-500/20">
          <div className="flex items-center gap-2 mb-1">
            <XCircle className="w-4 h-4 text-red-400" />
            <p className="text-sm text-red-300">Rejetées</p>
          </div>
          <p className="text-3xl font-bold text-red-400">{stats.rejected || 0}</p>
        </div>
      </div>

      {/* Main Panel */}
      <div className="bg-gray-800 rounded-xl border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-orange-400" />
            Propositions IA ({filteredProposals.length})
          </h2>
          <div className="flex gap-2">
            {[
              { id: 'pending_review', label: 'En attente', count: stats.pending_review },
              { id: 'approved', label: 'Approuvées', count: stats.approved },
              { id: 'rejected', label: 'Rejetées', count: stats.rejected },
              { id: 'all', label: 'Toutes', count: proposals.length },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1 text-sm rounded-lg transition ${
                  filter === f.id
                    ? 'bg-purple-500/20 text-purple-400'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
              >
                {f.label} ({f.count || 0})
              </button>
            ))}
          </div>
        </div>

        <div className="divide-y divide-gray-700 max-h-[500px] overflow-y-auto">
          {filteredProposals.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Lightbulb className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune proposition {filter === 'pending_review' ? 'en attente' : ''}</p>
              <p className="text-sm mt-1">Les propositions IA apparaîtront ici lors de classifications de nouveaux fournisseurs</p>
            </div>
          ) : (
            filteredProposals.map((proposal, idx) => (
              <div key={idx} className="p-4 hover:bg-gray-700/30 transition" data-testid={`proposal-${proposal.normalized_key}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusBadge(proposal.status)}
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        proposal.kind === 'supplier' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
                      }`}>
                        {proposal.kind === 'supplier' ? 'Fournisseur' : 'Client'}
                      </span>
                      <span className="font-medium text-white">{proposal.original_name}</span>
                    </div>

                    {/* Classification Info */}
                    <div className="bg-gray-700/50 rounded-lg p-3 mb-2">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-400">Catégorie:</span>{' '}
                          <span className="text-white">{proposal.mapping?.categorie || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Type:</span>{' '}
                          <span className="text-white">{proposal.mapping?.type_tiers || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Attribuable chantier:</span>{' '}
                          <span className={proposal.mapping?.est_attribuable_chantier ? 'text-green-400' : 'text-gray-400'}>
                            {proposal.mapping?.est_attribuable_chantier ? 'Oui' : 'Non'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-400">Confiance IA:</span>{' '}
                          <span className={`font-medium ${
                            proposal.confidence >= 0.7 ? 'text-green-400' : 
                            proposal.confidence >= 0.5 ? 'text-orange-400' : 'text-red-400'
                          }`}>
                            {Math.round(proposal.confidence * 100)}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* AI Explanation */}
                    {proposal.ai_explanation && (
                      <div className="text-sm text-gray-400 italic mb-2">
                        💡 {proposal.ai_explanation}
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Créé: {new Date(proposal.created_at).toLocaleString('fr-FR')}</span>
                      {proposal.approved_by && (
                        <span>Approuvé par: {proposal.approved_by}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {proposal.status === 'pending_review' && (
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => approveProposal(proposal)}
                        disabled={processingId === proposal.normalized_key}
                        className="flex items-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm transition disabled:opacity-50"
                        data-testid={`approve-${proposal.normalized_key}`}
                      >
                        {processingId === proposal.normalized_key ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <ThumbsUp className="w-4 h-4" />
                        )}
                        Approuver
                      </button>
                      <button
                        onClick={() => setShowRejectModal(proposal)}
                        disabled={processingId === proposal.normalized_key}
                        className="flex items-center gap-1 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm transition disabled:opacity-50"
                        data-testid={`reject-${proposal.normalized_key}`}
                      >
                        <ThumbsDown className="w-4 h-4" />
                        Rejeter
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <ThumbsDown className="w-5 h-5 text-red-400" />
              Rejeter la proposition
            </h3>
            <p className="text-gray-400 mb-4">
              Vous allez rejeter la proposition pour <strong className="text-white">{showRejectModal.original_name}</strong>
            </p>
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">Raison (optionnel)</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ex: Classification incorrecte, fournisseur mal identifié..."
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:border-red-500 focus:outline-none resize-none h-24"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition"
              >
                Annuler
              </button>
              <button
                onClick={() => rejectProposal(showRejectModal)}
                disabled={processingId === showRejectModal.normalized_key}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition flex items-center justify-center gap-2"
              >
                {processingId === showRejectModal.normalized_key ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <ThumbsDown className="w-4 h-4" />
                )}
                Confirmer le rejet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CONFIG SECTION
// ═══════════════════════════════════════════════════════════════

function ConfigSection({ config, onReload, setError }) {
  const [newKeyword, setNewKeyword] = useState('');
  const [activeList, setActiveList] = useState('general_charges_keywords');
  const [adding, setAdding] = useState(false);

  const addKeyword = async () => {
    if (!newKeyword.trim()) return;
    setAdding(true);
    try {
      await apiPost('/api/rachel-v2/config/keyword', {
        keyword: newKeyword.trim(),
        list_type: activeList
      });
      setNewKeyword('');
      await onReload();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const removeKeyword = async (keyword) => {
    try {
      await apiDelete(`/api/rachel-v2/config/keyword?keyword=${encodeURIComponent(keyword)}&list_type=${activeList}`);
      await onReload();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!config) return <div className="text-gray-400">Chargement...</div>;

  const lists = [
    { id: 'general_charges_keywords', label: 'Charges Générales', color: 'gray' },
    { id: 'client_attributable_keywords', label: 'Fournisseurs BTP', color: 'purple' },
  ];

  const currentList = config[activeList] || [];

  return (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-purple-400" />
          Configuration des Mots-clés
        </h2>
        
        {/* List selector */}
        <div className="flex gap-2 mb-4">
          {lists.map(list => (
            <button
              key={list.id}
              onClick={() => setActiveList(list.id)}
              className={`px-4 py-2 rounded-lg transition ${
                activeList === list.id
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
              }`}
            >
              {list.label} ({(config[list.id] || []).length})
            </button>
          ))}
        </div>

        {/* Add keyword */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
            placeholder="Nouveau mot-clé..."
            className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:border-purple-500 focus:outline-none"
          />
          <button
            onClick={addKeyword}
            disabled={adding || !newKeyword.trim()}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {adding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Ajouter
          </button>
        </div>

        {/* Keywords list */}
        <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
          {currentList.map((keyword, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 px-3 py-1 bg-gray-700 rounded-full text-sm group"
            >
              {keyword}
              <button
                onClick={() => removeKeyword(keyword)}
                className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Thresholds */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <h3 className="font-semibold mb-4">Seuils de confiance</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-700/50 p-4 rounded-lg">
            <p className="text-sm text-gray-400">Auto-apprentissage</p>
            <p className="text-2xl font-bold text-green-400">{Math.round((config.thresholds?.auto_learn || 0.7) * 100)}%</p>
          </div>
          <div className="bg-gray-700/50 p-4 rounded-lg">
            <p className="text-sm text-gray-400">Suggestion haute</p>
            <p className="text-2xl font-bold text-purple-400">{Math.round((config.thresholds?.suggestion_high || 0.7) * 100)}%</p>
          </div>
          <div className="bg-gray-700/50 p-4 rounded-lg">
            <p className="text-sm text-gray-400">Suggestion moyenne</p>
            <p className="text-2xl font-bold text-orange-400">{Math.round((config.thresholds?.suggestion_medium || 0.4) * 100)}%</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// LEARNINGS SECTION
// ═══════════════════════════════════════════════════════════════

function LearningsSection({ learnings, onReload, setError }) {
  const [filter, setFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);

  const deleteLearning = async (learning) => {
    setDeleting(learning.normalized_key);
    try {
      await apiDelete('/api/rachel-v2/learn', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: learning.kind,
          normalized_key: learning.normalized_key
        })
      });
      await onReload();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(null);
    }
  };

  const filteredLearnings = filter === 'all' 
    ? learnings 
    : learnings.filter(l => l.kind === filter);

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700">
      <div className="p-4 border-b border-gray-700 flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Brain className="w-5 h-5 text-purple-400" />
          Apprentissages ({learnings.length})
        </h2>
        <div className="flex gap-2">
          {['all', 'supplier', 'customer'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-sm rounded-lg transition ${
                filter === f
                  ? 'bg-purple-500/20 text-purple-400'
                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
              }`}
            >
              {f === 'all' ? 'Tous' : f === 'supplier' ? 'Fournisseurs' : 'Clients'}
            </button>
          ))}
        </div>
      </div>
      
      <div className="divide-y divide-gray-700 max-h-[500px] overflow-y-auto">
        {filteredLearnings.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Aucun apprentissage
          </div>
        ) : (
          filteredLearnings.map((learning, idx) => (
            <div key={idx} className="p-4 flex items-center justify-between hover:bg-gray-700/50">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    learning.kind === 'supplier' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
                  }`}>
                    {learning.kind}
                  </span>
                  <span className="font-medium">{learning.original_name}</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-400">
                  <span>{learning.mapping?.categorie || '-'}</span>
                  <span>•</span>
                  <span>{learning.source}</span>
                  <span>•</span>
                  <span>{Math.round(learning.confidence * 100)}% confiance</span>
                </div>
              </div>
              <button
                onClick={() => deleteLearning(learning)}
                disabled={deleting === learning.normalized_key}
                className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition"
              >
                {deleting === learning.normalized_key ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// AUDIT SECTION
// ═══════════════════════════════════════════════════════════════

function AuditSection({ auditLog }) {
  const getEventColor = (event) => {
    switch (event) {
      case 'learn': return 'bg-green-500/20 text-green-400';
      case 'unlearn': return 'bg-red-500/20 text-red-400';
      case 'decision': return 'bg-blue-500/20 text-blue-400';
      case 'config_update': return 'bg-purple-500/20 text-purple-400';
      case 'ai_proposal': return 'bg-orange-500/20 text-orange-400';
      case 'approve_proposal': return 'bg-green-500/20 text-green-400';
      case 'reject_proposal': return 'bg-red-500/20 text-red-400';
      case 'batch_classify': return 'bg-cyan-500/20 text-cyan-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getEventLabel = (event) => {
    switch (event) {
      case 'ai_proposal': return '💡 Proposition IA';
      case 'approve_proposal': return '✅ Approbation';
      case 'reject_proposal': return '❌ Rejet';
      case 'batch_classify': return '📦 Batch';
      default: return event;
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700">
      <div className="p-4 border-b border-gray-700">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <History className="w-5 h-5 text-purple-400" />
          Audit Log ({auditLog.length})
        </h2>
      </div>
      
      <div className="divide-y divide-gray-700 max-h-[500px] overflow-y-auto">
        {auditLog.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Aucun événement
          </div>
        ) : (
          auditLog.map((log, idx) => (
            <div key={idx} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs rounded ${getEventColor(log.event)}`}>
                    {getEventLabel(log.event)}
                  </span>
                  <span className="text-sm font-medium">
                    {log.entity_ref?.type}:{log.entity_ref?.id}
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(log.created_at).toLocaleString('fr-FR')}
                </span>
              </div>
              {log.payload && Object.keys(log.payload).length > 0 && (
                <pre className="text-xs text-gray-400 bg-gray-700/50 p-2 rounded overflow-x-auto">
                  {JSON.stringify(log.payload, null, 2)}
                </pre>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// STATS SECTION
// ═══════════════════════════════════════════════════════════════

function StatsSection({ stats, config }) {
  return (
    <div className="space-y-6">
      {/* Knowledge Stats */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-purple-400" />
          Base de Connaissances
        </h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 p-4 rounded-xl border border-blue-500/20">
            <p className="text-sm text-blue-300">Fournisseurs</p>
            <p className="text-3xl font-bold text-blue-400">{stats?.knowledge?.suppliers_known || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 p-4 rounded-xl border border-green-500/20">
            <p className="text-sm text-green-300">Clients</p>
            <p className="text-3xl font-bold text-green-400">{stats?.knowledge?.customers_known || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 p-4 rounded-xl border border-purple-500/20">
            <p className="text-sm text-purple-300">Total appris</p>
            <p className="text-3xl font-bold text-purple-400">{stats?.knowledge?.total_learnings || 0}</p>
          </div>
        </div>
      </div>

      {/* Config Stats */}
      {config && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-purple-400" />
            Configuration Actuelle
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-700/50 p-4 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Mots-clés Charges Générales</p>
              <p className="text-2xl font-bold">{config.general_charges_keywords?.length || 0}</p>
            </div>
            <div className="bg-gray-700/50 p-4 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Mots-clés BTP</p>
              <p className="text-2xl font-bold">{config.client_attributable_keywords?.length || 0}</p>
            </div>
            <div className="bg-gray-700/50 p-4 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Industry Pack</p>
              <p className="text-xl font-bold text-purple-400">{config.industry_pack?.toUpperCase()}</p>
            </div>
            <div className="bg-gray-700/50 p-4 rounded-lg">
              <p className="text-sm text-gray-400 mb-1">Standard Comptable</p>
              <p className="text-xl font-bold text-blue-400">{config.accounting_standard || 'PCG_FR'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Decisions Stats */}
      {stats?.decisions && Object.keys(stats.decisions).length > 0 && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-purple-400" />
            Décisions par Status
          </h2>
          <div className="grid grid-cols-4 gap-4">
            {Object.entries(stats.decisions).map(([status, count]) => (
              <div key={status} className="bg-gray-700/50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-sm text-gray-400">{status}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
